import {
  ASK_CANNOT_ANSWER_LABEL,
  type AskCannotAnswerResult,
  type AskExecutableResult,
  type AskResult,
  type AskRouteToViewResult,
  type QueryRunFilter,
} from '@console/types'
import { useNavigate } from 'react-router-dom'
import { formatInstant } from '../format.js'
import { searchParamsForView } from '../runs/url.js'
import { Button, HairlineRule, Stamp, TrendChart } from '../ui/index.js'
import { describeFilter, runsUrlWouldLoseMeaning, toConsoleRunFilter } from './filter-mapping.js'
import styles from './renderers.module.css'

/**
 * One renderer per `AskResult` kind (spec §5).
 *
 * The rule they all obey: **no synthesized prose about data.** Every word an
 * operator reads here is either their own question, a label built from the
 * query's own fields, or the model's template-constrained refusal text. Two
 * numbers sit side by side and the operator draws the conclusion; nothing
 * here ever says one is better than the other.
 */

// ---------------------------------------------------------------------------
// Click-through. Every result kind can open the Runs list filtered to what it
// was computed from — the operator seeing the underlying runs is what keeps
// an aggregate from being a number they have to take on faith.
// ---------------------------------------------------------------------------

function useRunsNavigation() {
  const navigate = useNavigate()
  return (filter: QueryRunFilter) => {
    if (runsUrlWouldLoseMeaning(filter)) return
    const params = searchParamsForView(toConsoleRunFilter(filter), 1, 'time', 'desc')
    navigate(`/runs?${params.toString()}`)
  }
}

export function AsOfLabel({ asOf }: { asOf: string }) {
  // Same fix as `AsOfLine` and the runs table — `LGH-024`.
  return <span className={styles['asOf']}>as of {formatInstant(asOf)}</span>
}

// ---------------------------------------------------------------------------
// §5.2 Aggregate
// ---------------------------------------------------------------------------

interface AggregateBucketish {
  group: string | null
  currency: string | null
  value: number
  run_count: number
}

function formatValue(bucket: AggregateBucketish): string {
  const rounded = Number.isInteger(bucket.value) ? bucket.value : Number(bucket.value.toFixed(2))
  if (bucket.currency === null) return rounded.toLocaleString()
  return `${bucket.currency} ${rounded.toLocaleString()}`
}

export function AggregateResultRenderer({
  result,
  buckets,
  asOf,
}: {
  result: Extract<AskExecutableResult, { kind: 'aggregate' }>
  buckets: AggregateBucketish[] | null
  asOf: string
}) {
  const openRuns = useRunsNavigation()
  const label = `${result.function} of ${result.field ?? 'runs'} · ${describeFilter(result.filter)}`

  // Null, not empty: the aggregate is undefined, not zero. "0" would be a
  // claim about the data that no run supports.
  if (buckets === null || buckets.length === 0) {
    return (
      <div className={styles['card']}>
        <p className={styles['empty']}>No matching runs</p>
        <p className={styles['label']}>{label}</p>
        <AsOfLabel asOf={asOf} />
      </div>
    )
  }

  const grouped = buckets.length > 1 || buckets[0]?.group !== null

  return (
    <div className={styles['card']}>
      {grouped ? (
        // A vertical list, never a bar chart: visual complexity rarely earns
        // its keep for a handful of grouped numbers (spec §5.2).
        <dl className={styles['groupList']}>
          {buckets.map((bucket) => (
            <div key={bucket.group ?? 'all'} className={styles['groupRow']}>
              <dt>{bucket.group ?? 'All runs'}</dt>
              <dd className={styles['groupValue']}>
                {formatValue(bucket)}
                <span className={styles['runCount']}>{bucket.run_count} runs</span>
              </dd>
            </div>
          ))}
        </dl>
      ) : (
        <button
          type="button"
          className={styles['bigNumberButton']}
          onClick={() => {
            openRuns(result.filter)
          }}
        >
          <span className={styles['bigNumber']}>
            {buckets[0] === undefined ? 'No matching runs' : formatValue(buckets[0])}
          </span>
        </button>
      )}
      <p className={styles['label']}>
        {label} · {buckets.reduce((sum, b) => sum + b.run_count, 0)} runs
      </p>
      <AsOfLabel asOf={asOf} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// §5.3 Breakdown
// ---------------------------------------------------------------------------

interface BreakdownRowish {
  group: string
  count: number
  total_amount: number | null
  currency: string | null
}

export function BreakdownResultRenderer({
  result,
  rows,
  asOf,
}: {
  result: Extract<AskExecutableResult, { kind: 'breakdown' }>
  rows: BreakdownRowish[]
  asOf: string
}) {
  const openRuns = useRunsNavigation()

  if (rows.length === 0) {
    return (
      <div className={styles['card']}>
        <p className={styles['empty']}>No matching runs</p>
        <p className={styles['label']}>by {result.groupBy}</p>
        <AsOfLabel asOf={asOf} />
      </div>
    )
  }

  const magnitudeOf = (row: BreakdownRowish) =>
    result.orderBy === 'sum' ? (row.total_amount ?? 0) : row.count
  const max = Math.max(...rows.map(magnitudeOf), 1)

  return (
    <div className={styles['card']}>
      <ul className={styles['rankedList']}>
        {rows.map((row) => (
          <li key={row.group}>
            <button
              type="button"
              className={styles['rankedRow']}
              onClick={() => {
                // The row's own group value, added to the filter it came from.
                openRuns({ ...result.filter, customer: row.group })
              }}
            >
              <span className={styles['rankedLabel']}>{row.group}</span>
              <span
                className={styles['rankedBar']}
                style={{ width: `${String((magnitudeOf(row) / max) * 100)}%` }}
                aria-hidden="true"
              />
              <span className={styles['rankedValue']}>
                {result.orderBy === 'sum' && row.total_amount !== null
                  ? `${row.currency ?? ''} ${row.total_amount.toLocaleString()}`.trim()
                  : row.count}
              </span>
            </button>
          </li>
        ))}
      </ul>
      <p className={styles['label']}>
        by {result.groupBy} · ranked by {result.orderBy} · {describeFilter(result.filter)}
      </p>
      <AsOfLabel asOf={asOf} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// §5.4 Timeline
// ---------------------------------------------------------------------------

interface TimelineBucketish {
  period_start: string
  count: number
  total_amount: number | null
  currency: string | null
}

export function TimelineResultRenderer({
  result,
  buckets,
  asOf,
}: {
  result: Extract<AskExecutableResult, { kind: 'timeline' }>
  buckets: TimelineBucketish[]
  asOf: string
}) {
  const openRuns = useRunsNavigation()

  if (buckets.length === 0) {
    return (
      <div className={styles['card']}>
        <p className={styles['empty']}>No matching runs</p>
        <AsOfLabel asOf={asOf} />
      </div>
    )
  }

  // Same hairline treatment as Health's 12-week trends (spec §5.4). `weekStart`
  // is TrendChart's field name; the period here may be a day or a month.
  const points = buckets.map((b) => ({
    weekStart: b.period_start,
    value: b.count,
    isAnomaly: false,
  }))

  return (
    <div className={styles['card']}>
      <TrendChart
        label={`runs per ${result.granularity}`}
        points={points}
        formatValue={(v) => String(v)}
        onPointClick={(index) => {
          const bucket = buckets[index]
          if (bucket === undefined) return
          const next = new Date(bucket.period_start)
          const step = { day: 1, week: 7, month: 30 }[result.granularity]
          next.setDate(next.getDate() + step)
          openRuns({
            ...result.filter,
            date_from: bucket.period_start,
            date_to: next.toISOString(),
          })
        }}
      />
      <p className={styles['label']}>{describeFilter(result.filter)}</p>
      <AsOfLabel asOf={asOf} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// §5.6 Route to view
// ---------------------------------------------------------------------------

/** Where each `view` lands. Params are the model's; the paths are ours. */
export function routeToViewPath(result: AskRouteToViewResult): string {
  switch (result.view) {
    case 'run_detail': {
      const id = result.params['runId'] ?? result.params['reference'] ?? ''
      return id === '' ? '/runs' : `/runs/${encodeURIComponent(id)}`
    }
    case 'health_breakdown':
      return '/health'
    case 'runs_filtered': {
      const params = new URLSearchParams(result.params)
      return `/runs?${params.toString()}`
    }
    case 'rules':
      // The Rules surface does not exist yet (addendum §I deferred it). Send
      // the operator to Health, which is where policy gaps are reported —
      // and which says plainly that Rules is not built.
      return '/health'
  }
}

export function RouteToViewRenderer({ result }: { result: AskRouteToViewResult }) {
  const navigate = useNavigate()
  const path = routeToViewPath(result)

  return (
    <div className={styles['card']}>
      {/* The model's reason line — why this destination, never what's there. */}
      <p>{result.reason}</p>
      <Button
        variant="primary"
        onClick={() => {
          navigate(path)
        }}
      >
        Go to {result.view.replace(/_/g, ' ')} →
      </Button>
      {/* No auto-navigation: the click is where the operator confirms the
          routing was right (spec §5.6). */}
      <p className={styles['label']}>
        Ask doesn&apos;t answer this itself — the answer lives there.
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// §5.7 Cannot answer
// ---------------------------------------------------------------------------

export function CannotAnswerRenderer({
  result,
  onSuggestionClick,
}: {
  result: AskCannotAnswerResult
  /** Populates the Ask box. Never executes — the operator confirms first. */
  onSuggestionClick: (question: string) => void
}) {
  return (
    <div className={styles['refusal']}>
      <div className={styles['refusalHeader']}>
        <Stamp variant="warn" />
        <span className={styles['refusalTitle']}>I can&apos;t answer this</span>
      </div>

      <p className={styles['refusalReason']}>{ASK_CANNOT_ANSWER_LABEL[result.reason]}</p>
      <p className={styles['refusalExplanation']}>{result.explanation}</p>

      <HairlineRule />

      <p className={styles['label']}>You might want to try:</p>
      <ul className={styles['suggestions']}>
        {result.suggested_queries.map((suggestion, index) => {
          const question = suggestion.question ?? describeFilter(suggestion.filter)
          return (
            <li key={`${question}-${String(index)}`}>
              <Button
                variant="secondary"
                onClick={() => {
                  onSuggestionClick(question)
                }}
              >
                {question}
              </Button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Kind label, for the result page's header.
// ---------------------------------------------------------------------------

export const ASK_KIND_LABEL: Record<AskResult['kind'], string> = {
  filter: 'Runs',
  aggregate: 'A single number',
  breakdown: 'A ranked list',
  timeline: 'Over time',
  compare: 'Side by side',
  route_to_view: 'Routing',
  cannot_answer: 'No answer',
}
